/**
 * SortBySelector - Reusable sort control with two display modes
 *
 * Supports both segmented buttons (for few options) and dropdown (for many options).
 * Label sits outside the control box in both modes.
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';
import { FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';

import Dropdown from '@/components/Dropdown/Dropdown';

export type SortDirection = 'asc' | 'desc';

export interface SortOption<T extends string> {
  value: T;
  label: string;
}

export interface SortBySelectorProps<T extends string> {
  /** Currently selected sort field */
  value: T;
  /** Current sort direction */
  direction: SortDirection;
  /** Available sort options */
  options: SortOption<T>[];
  /** Called when field or direction changes */
  onChange: (value: T, direction: SortDirection) => void;
  /** Display variant: 'buttons' for segmented control, 'dropdown' for select menu */
  variant?: 'buttons' | 'dropdown';
  /** Optional label (defaults to "Sort:") */
  label?: string;
  /** Additional CSS classes */
  className?: string;
}

export default function SortBySelector<T extends string>({
  value,
  direction,
  options,
  onChange,
  variant = 'buttons',
  label = 'Sort:',
  className,
}: SortBySelectorProps<T>): ReactElement {
  const handleFieldChange = (newValue: T | null) => {
    if (newValue && newValue !== value) {
      onChange(newValue, 'asc');
    }
  };

  const handleFieldClick = (field: T) => {
    if (value === field) {
      // Toggle direction
      onChange(field, direction === 'asc' ? 'desc' : 'asc');
    } else {
      onChange(field, 'asc');
    }
  };

  const handleDirectionToggle = () => {
    onChange(value, direction === 'asc' ? 'desc' : 'asc');
  };

  if (variant === 'dropdown') {
    return (
      <div className={clsx('flex items-center gap-2', className)}>
        <span className="text-xs text-zinc-500">{label}</span>
        <div className="flex items-center gap-1">
          <Dropdown value={value} options={options} onChange={handleFieldChange} size="sm" aria-label={`Sort by`} />
          {/* Direction toggle */}
          <button
            type="button"
            onClick={handleDirectionToggle}
            className="rounded-md border border-zinc-700 bg-zinc-800/50 p-1.5 text-zinc-400 transition-colors hover:bg-zinc-700/50 hover:text-zinc-200"
            aria-label={direction === 'asc' ? 'Sort ascending' : 'Sort descending'}
          >
            {direction === 'asc' ? <FaSortAmountUp className="h-3.5 w-3.5" /> : <FaSortAmountDown className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
    );
  }

  // Buttons variant (default)
  return (
    <div className={clsx('flex items-center gap-2', className)}>
      <span className="text-xs text-zinc-500">{label}</span>
      <div className="inline-flex items-center rounded-md border border-zinc-700 bg-zinc-800/50 p-0.5">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => handleFieldClick(opt.value)}
            className={clsx(
              'rounded px-2.5 py-1 text-xs font-medium transition-all',
              value === opt.value ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-400 hover:text-zinc-200',
            )}
          >
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          onClick={handleDirectionToggle}
          className="ml-0.5 rounded px-1.5 py-1 text-zinc-400 transition-colors hover:text-zinc-200"
          aria-label={direction === 'asc' ? 'Sort ascending' : 'Sort descending'}
        >
          {direction === 'asc' ? <FaSortAmountUp className="h-3 w-3" /> : <FaSortAmountDown className="h-3 w-3" />}
        </button>
      </div>
    </div>
  );
}
